import { clamp } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * How much the zoom panel magnifies the source image. Also determines the
 * lens size (the lens is `100 / ZOOM_FACTOR`% of the image on each axis), so
 * a higher factor means both a smaller lens square and a more magnified
 * panel — matching Amazon's fairly small, tightly-cropped lens.
 */
const ZOOM_FACTOR = 6;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The original product image being magnified.
 */

/**
 * Amazon-style hover magnifier: while the shopper hovers the original product
 * image with a mouse, a lens outline follows the cursor over the image and a
 * matching magnified crop is displayed in an adjacent panel to the right.
 * Distinct from the click-to-open fullscreen `<zoom-dialog>`, which keeps
 * working independently (this component never intercepts clicks).
 *
 * For video/external_video media the magnifier zooms the still poster image
 * before playback starts. Once playback is actually running the magnifier is
 * disabled — a playing video has no single frame to read, and none of this
 * theme's video events give a clean "the picture just changed" signal to
 * refresh a crop from. But while the shopper has paused the video (via the
 * theme's hover-revealed pause button, or once it reaches the end), the
 * paused frame IS a single stable picture again, so the magnifier reads it
 * fresh from the `<video>` element itself via a canvas capture each time the
 * shopper hovers. An external_video (YouTube/Vimeo) iframe never exposes its
 * pixels cross-origin, so it only ever gets the poster-based zoom.
 *
 * @extends {Component<Refs>}
 */
export class HoverZoomMedia extends Component {
  requiredRefs = ['image'];

  /** @type {AbortController | undefined} */
  #controller;
  /** @type {HTMLElement | null} */
  #lens = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  #hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /**
   * Reused across hovers so repeatedly opening the magnifier on a paused
   * video doesn't allocate a new canvas every time.
   * @type {HTMLCanvasElement | undefined}
   */
  #captureCanvas;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#lens = this.querySelector('[data-hover-zoom-lens]');
    this.#panel = this.querySelector('[data-hover-zoom-panel]');
    if (!this.#lens || !this.#panel) return;

    // The gallery's carousel/slide containers clip overflow (so only the
    // active slide shows), which would crop the zoom panel since it's meant
    // to float outside the original image's box. Move it to <body> and
    // position it with `fixed` coordinates instead, so it renders above
    // everything regardless of any ancestor's overflow or stacking context.
    this.#panel.remove();
    document.body.appendChild(this.#panel);

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerenter', this.#handleEnter, { signal });
    this.addEventListener('pointermove', this.#handleMove, { signal });
    this.addEventListener('pointerleave', this.#handleLeave, { signal });

    // The real <video> element only exists once the shopper presses play —
    // the theme keeps it inside a <template> until then (see
    // `#getZoomSourceUrl`) — so these are all capture-phase listeners on
    // this component itself (HTMLMediaElement's play/pause/ended events
    // don't bubble) rather than a one-time lookup at connect time.
    this.addEventListener('play', this.#handleVideoStateChange, { signal, capture: true });
    this.addEventListener('pause', this.#handleVideoStateChange, { signal, capture: true });
    this.addEventListener('ended', this.#handleVideoStateChange, { signal, capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#panel?.remove();
  }

  /**
   * Keeps `#video` up to date and immediately drops the lens/panel the
   * instant playback (re)starts, rather than waiting for the next pointer
   * event to notice.
   */
  #handleVideoStateChange = () => {
    this.#video = this.querySelector('video');
    if (this.#isVideoPlaying()) {
      this.classList.add('is-video-playing');
      this.#handleLeave();
    } else {
      this.classList.remove('is-video-playing');
    }
  };

  #isVideoPlaying() {
    return !!this.#video && !this.#video.paused && !this.#video.ended;
  }

  /**
   * @param {PointerEvent} event
   */
  #handleEnter = (event) => {
    if (this.#isVideoPlaying() || event.pointerType !== 'mouse' || !this.#hoverCapable.matches || !this.#panel) {
      return;
    }

    const highResUrl = this.#getZoomSourceUrl();
    if (!highResUrl) return;

    this.#panel.style.backgroundImage = `url("${highResUrl}")`;
    const contentRect = this.#getVisibleContentRect();
    this.#positionPanel(contentRect);
    this.classList.add('is-zooming');
    this.#panel.classList.add('is-visible');
  };

  /**
   * @returns {string | undefined}
   */
  #getZoomSourceUrl() {
    // Once the shopper has paused a (real, same-origin) <video>, read the
    // paused frame itself — freshly, on every hover, in case they scrubbed
    // to a different point while paused — rather than the static poster,
    // since the poster no longer matches what's on screen.
    if (this.#video && !this.#isVideoPlaying()) {
      const capturedFrame = this.#captureVideoFrame(this.#video);
      if (capturedFrame) return capturedFrame;
      // A capture failure (video not ready yet, or a cross-origin source
      // the canvas can't read back) falls through to the poster below,
      // which — for a paused video — is still the same still image the
      // shopper saw before pressing play, so it's a reasonable stand-in
      // rather than showing no zoom at all.
    }

    // Note: the theme's own `product-media` snippet sets this via Shopify's
    // `data_max_resolution` image_tag parameter, which — despite the
    // underscore naming convention normally used for Liquid filter params —
    // renders literally as a `data_max_resolution="..."` HTML attribute
    // (underscore, not hyphen). `element.dataset` only exposes HTML5
    // `data-*` (hyphenated) attributes, so `this.#image.dataset.maxResolution`
    // always reads as `undefined` here and silently fell back to whatever
    // (lower-resolution, viewport-sized) variant `currentSrc` happened to be
    // — this was the real cause of the zoom looking softer than opening the
    // original file. Reading the literal attribute name gets the full
    // `width: 3840` source the theme actually provides.
    return (
      this.#image.getAttribute('data_max_resolution') || this.#image.currentSrc || this.#image.src
    );
  }

  /**
   * Draws the video's current frame into an offscreen canvas and reads it
   * back as a data URL, at the video's own decoded resolution (never
   * upscaled or downscaled) so the magnifier is showing the sharpest version
   * of that frame the browser has. `image/png` is used instead of the
   * default JPEG to avoid stacking a second, lossy re-compression on top of
   * the video codec's own compression — the frame is already softer than a
   * dedicated product photo (most product videos are recorded at far lower
   * resolution than the 3840px photos above), and PNG at least doesn't make
   * that worse. This is a hard resolution ceiling, not a bug: zooming into a
   * lower-resolution source always looks softer than zooming into a photo,
   * the same way it would in any photo/video viewer.
   * @param {HTMLVideoElement} video
   * @returns {string | undefined}
   */
  #captureVideoFrame(video) {
    // HAVE_CURRENT_DATA (2): the frame at the current position is decoded
    // and available. Below that (e.g. right as playback is toggled), drawing
    // would capture a blank or stale frame.
    if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) return undefined;

    this.#captureCanvas ??= document.createElement('canvas');
    const canvas = this.#captureCanvas;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    try {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } catch {
      // A cross-origin video source without permissive CORS headers taints
      // the canvas and throws on read-back (toDataURL) rather than on draw.
      return undefined;
    }
  }

  /**
   * Places the (now `<body>`-level) panel immediately to the right of the
   * image's actual visible picture (not the, possibly letterboxed, element
   * box — see `#getVisibleContentRect`), matching its size, using
   * viewport-relative `fixed` coordinates so it tracks the image regardless
   * of page scroll position.
   * @param {{left: number, top: number, width: number, height: number}} contentRect
   */
  #positionPanel(contentRect) {
    if (!this.#panel) return;

    const gap = 16;

    this.#panel.style.top = `${contentRect.top}px`;
    this.#panel.style.left = `${contentRect.left + contentRect.width + gap}px`;
    this.#panel.style.width = `${contentRect.width}px`;
    this.#panel.style.height = `${contentRect.height}px`;
  }

  /**
   * @param {PointerEvent} event
   */
  #handleMove = (event) => {
    if (this.#isVideoPlaying() || event.pointerType !== 'mouse' || !this.classList.contains('is-zooming')) return;
    if (!this.#lens || !this.#panel) return;

    const contentRect = this.#getVisibleContentRect();
    if (!contentRect.width || !contentRect.height) return;

    this.#positionPanel(contentRect);

    // The lens is positioned in pixels relative to this component's own box
    // (its `position: relative` ancestor), rather than as a percentage of
    // it, because the visible picture (`contentRect`) can be smaller than —
    // and offset within — that box whenever `object-fit: contain`
    // letterboxes the image. A percentage-based lens position was computed
    // against `contentRect` but rendered against the full (larger) box,
    // silently drifting the lens away from the cursor and, worse, away from
    // the region the panel actually magnifies. Working in pixels against
    // `contentRect` directly keeps the lens, the cursor, and the magnified
    // crop all in agreement.
    const wrapperRect = this.getBoundingClientRect();
    const lensWidthPx = contentRect.width / ZOOM_FACTOR;
    const lensHeightPx = contentRect.height / ZOOM_FACTOR;

    const lensLeftPx = clamp(
      event.clientX - contentRect.left - lensWidthPx / 2,
      0,
      contentRect.width - lensWidthPx
    );
    const lensTopPx = clamp(
      event.clientY - contentRect.top - lensHeightPx / 2,
      0,
      contentRect.height - lensHeightPx
    );

    this.#lens.style.width = `${lensWidthPx}px`;
    this.#lens.style.height = `${lensHeightPx}px`;
    this.#lens.style.left = `${contentRect.left - wrapperRect.left + lensLeftPx}px`;
    this.#lens.style.top = `${contentRect.top - wrapperRect.top + lensTopPx}px`;

    // Percentage-based background-position/-size are still correct here
    // because the panel's own box now exactly matches `contentRect` (see
    // `#positionPanel`), so this maps 1:1 onto the same region the lens is
    // covering above.
    const horizontalRangePx = contentRect.width - lensWidthPx;
    const verticalRangePx = contentRect.height - lensHeightPx;
    const backgroundPositionX = horizontalRangePx === 0 ? 0 : (lensLeftPx / horizontalRangePx) * 100;
    const backgroundPositionY = verticalRangePx === 0 ? 0 : (lensTopPx / verticalRangePx) * 100;

    this.#panel.style.backgroundSize = `${ZOOM_FACTOR * 100}% ${ZOOM_FACTOR * 100}%`;
    this.#panel.style.backgroundPosition = `${backgroundPositionX}% ${backgroundPositionY}%`;
  };

  #handleLeave = () => {
    this.classList.remove('is-zooming');
    this.#panel?.classList.remove('is-visible');
  };

  /**
   * The `<img>` element itself may be larger than the visible picture when
   * `object-fit: contain` letterboxes it, so the lens/zoom math is done
   * against the actual visible image content rather than the element box.
   * The same box also frames the (same-sized, same-position) `<video>` once
   * one is playing, so this is reused as-is for the paused-video capture.
   * @returns {{left: number, top: number, width: number, height: number}}
   */
  #getVisibleContentRect() {
    const rect = this.#image.getBoundingClientRect();
    const { naturalWidth, naturalHeight } = this.#image;
    const isContained = getComputedStyle(this.#image).objectFit === 'contain';

    if (!isContained || !naturalWidth || !naturalHeight || !rect.width || !rect.height) {
      return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    }

    const boxRatio = rect.width / rect.height;
    const imageRatio = naturalWidth / naturalHeight;

    let { width, height } = rect;
    let { left, top } = rect;

    if (imageRatio > boxRatio) {
      height = rect.width / imageRatio;
      top = rect.top + (rect.height - height) / 2;
    } else {
      width = rect.height * imageRatio;
      left = rect.left + (rect.width - width) / 2;
    }

    return { left, top, width, height };
  }
}

if (!customElements.get('hover-zoom-media')) {
  customElements.define('hover-zoom-media', HoverZoomMedia);
}
